import { useEffect, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Image as ImageIcon } from "lucide-react";
import SEO from "@/components/SEO";
import RichEditor from "@/components/admin/RichEditor";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const emptyForm = {
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    image: "",
};

export default function AdminBlogs() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);

    const [form, setForm] = useState(emptyForm);

    const [editId, setEditId] = useState(null);

    const [file, setFile] = useState(null);

    const [busy, setBusy] = useState(false);

    // ================= LOAD BLOGS =================
    const loadBlogs = async () => {
        try {
            const res = await api.get("/blogs");

            setItems(res.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    useEffect(() => {
        loadBlogs();
    }, []);

    // ================= INPUT CHANGE =================
    const setF = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    // ================= OPEN NEW =================
    const openNew = () => {
        setForm(emptyForm);
        setFile(null);
        setEditId(null);
        setOpen(true);
    };

    // ================= OPEN EDIT =================
    const openEdit = (blog) => {
        setForm({
            title: blog.title || "",
            slug: blog.slug || "",
            excerpt: blog.excerpt || "",
            content: blog.content || "",
            image: blog.cover_image || "",
        });

        setEditId(blog.id);
        setFile(null);
        setOpen(true);
    };

    // ================= DELETE =================
    const deleteBlog = async (id) => {
        const confirmDelete = confirm("Delete this blog?");

        if (!confirmDelete) return;

        try {
            await api.delete(`/admin/blogs/${id}`);

            toast.success("Blog deleted");

            loadBlogs();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    // ================= SAVE BLOG =================
    const saveBlog = async (e) => {
        e.preventDefault();

        setBusy(true);

        try {
            const formData = new FormData();

            formData.append("title", form.title);
            formData.append("slug", form.slug);
            formData.append("excerpt", form.excerpt);
            formData.append("content", form.content);
            formData.append("category", "general");

            if (file) {
                formData.append("cover_image", file);
            }

            if (editId) {
                await api.put(`/admin/blogs/${editId}`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                toast.success("Blog updated");
            } else {
                await api.post(`/admin/blogs`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                toast.success("Blog created");
            }

            setOpen(false);

            setForm(emptyForm);

            setFile(null);

            loadBlogs();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // ================= IMAGE URL =================
    const fullUrl = (url) => {
        if (!url) return "";

        return url.startsWith("/")
            ? `${BACKEND_URL}${url}`
            : url;
    };

    return (
        <div>
            <SEO title="Admin - Blog" />
            {/* HEADER */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">
                    Blogs
                </h1>

                <button
                    onClick={openNew}
                    className="bg-black text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />

                    Add Blog
                </button>
            </div>

            {/* BLOG LIST */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((blog) => (
                    <div
                        key={blog.id}
                        className="bg-white border rounded-2xl overflow-hidden"
                    >
                        {/* IMAGE */}
                        {blog.cover_image ? (
                            <img
                                src={fullUrl(blog.cover_image)}
                                alt={blog.title}
                                className="w-full h-40 sm:h-52 object-cover"
                            />
                        ) : (
                            <div className="w-full h-40 sm:h-52 bg-gray-100 flex items-center justify-center">
                                <ImageIcon className="w-10 h-10 text-gray-400" />
                            </div>
                        )}

                        {/* CONTENT */}
                        <div className="p-5">
                            <h2 className="font-bold text-lg">
                                {blog.title}
                            </h2>

                            <p className="text-sm text-gray-500 mt-1">
                                {blog.slug}
                            </p>

                            <p className="text-sm text-gray-700 mt-3 line-clamp-3">
                                {blog.excerpt}
                            </p>

                            {/* ACTIONS */}
                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={() => openEdit(blog)}
                                    className="p-2 rounded hover:bg-gray-100"
                                >
                                    <Edit className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={() =>
                                        deleteBlog(blog.id)
                                    }
                                    className="p-2 rounded text-red-500 hover:bg-red-50"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* MODAL */}
            <Dialog
                open={open}
                onOpenChange={setOpen}
            >
                <DialogContent className="max-w-2xl w-[95vw] sm:w-full z-[100] max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editId
                                ? "Edit Blog"
                                : "Add Blog"}
                        </DialogTitle>
                    </DialogHeader>

                    <form
                        onSubmit={saveBlog}
                        className="space-y-4"
                    >
                        {/* IMAGE */}
                        <div>
                            <label className="text-sm font-medium">
                                Upload Image
                            </label>

                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                    setFile(
                                        e.target.files[0]
                                    )
                                }
                                className="mt-2 block text-sm w-full"
                            />

                            {(file || form.image) && (
                                <img
                                    src={
                                        file
                                            ? URL.createObjectURL(
                                                  file
                                              )
                                            : fullUrl(
                                                  form.image
                                              )
                                    }
                                    alt=""
                                    className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg mt-3"
                                />
                            )}
                        </div>

                        {/* TITLE */}
                        <input
                            type="text"
                            placeholder="Blog Title"
                            value={form.title}
                            onChange={(e) =>
                                setF(
                                    "title",
                                    e.target.value
                                )
                            }
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                            required
                        />

                        {/* SLUG */}
                        <input
                            type="text"
                            placeholder="Slug"
                            value={form.slug}
                            onChange={(e) =>
                                setF(
                                    "slug",
                                    e.target.value
                                )
                            }
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                        />

                        {/* EXCERPT */}
                        <textarea
                            placeholder="Short Description"
                            value={form.excerpt}
                            onChange={(e) =>
                                setF(
                                    "excerpt",
                                    e.target.value
                                )
                            }
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                            rows={3}
                        />

                        {/* CONTENT */}
                        <div>
                            <label className="text-sm font-medium mb-1 block">Content</label>
                            <RichEditor
                                value={form.content}
                                onChange={(v) => setF("content", v)}
                            />
                        </div>

                        {/* BUTTONS */}
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() =>
                                    setOpen(false)
                                }
                                className="px-4 py-2 border rounded-lg text-sm"
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={busy}
                                className="bg-black text-white px-4 py-2 rounded-lg text-sm"
                            >
                                {busy
                                    ? "Saving..."
                                    : "Save"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}